import React, { useEffect, useState } from "react";
import DepartmentBanner from "../common/DepartmentBanner";
import FacultyStaffService from "../../services/Department/FacultyStaffService";
import OfficeHourService from "../../services/Department/OfficeHourService";
import ClassScheduleService from "../../services/Department/ClassScheduleService";
import styles from './FacultyAndStaff.module.css';
import { FaSearch, FaSpinner, FaTable, FaTh, FaSort, FaSortUp, FaSortDown, FaUserTie, FaEnvelope, FaPhone, FaBriefcase } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Pagination from '../common/Pagination';
import TermSelector from '../common/TermSelector';
import DepartmentService from "../../services/Department/DepartmentService";

const FacultyAndStaff = () => {
  document.title = "Faculty And Staff";
  const params = new URLSearchParams(window.location.search);


  const [isLoading, setIsLoading] = useState(false);
  const [facultyStaffs, setFacultyStaffs] = useState([]);
  const [officeHours, setOfficeHours] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState('table');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [disabledColumns, setDisabledColumns] = useState([]);
  const itemsPerPage = 20;
  const [openAccordion, setOpenAccordion] = useState(null);
  const [accordionOfficeHours, setAccordionOfficeHours] = useState([]);
  const [accordionClassSchedule, setAccordionClassSchedule] = useState([]);
  const [classSchedule, setClassSchedule] = useState([]);
  const [accordionLoading, setAccordionLoading] = useState(false);
  const [accordionTerm, setAccordionTerm] = useState(0);
  const [accordionTermDetails, setAccordionTermDetails] = useState({});
  const [hasLoadedInitialTerm, setHasLoadedInitialTerm] = useState(false);
  const [openAccordionType, setOpenAccordionType] = useState(null);
  const [selectedFacultyId, setSelectedFacultyId] = useState(null);
  const [termSelectorKey, setTermSelectorKey] = useState(0);

  // Define column types
  const COLUMN_TYPES = Object.freeze({
    NAME_AND_CONTACT: 16,
    CONTACT_INFORMATION: 17,
    ACTION: 18
  });

  // Define valid sort keys as a frozen object for immutability
  const SORT_KEYS = Object.freeze({
    NAME: 'fullName',
    EMAIL: 'email',
    PHONE: 'officePhone',
    TITLE: 'userTypeSt',
    CONTACT: 'contractInfo'
  });

  // Check if a column is enabled
  const isColumnEnabled = (columnType) => {
    return !disabledColumns.includes(columnType);
  };

  useEffect(() => {
    
      // Get column configuration
      DepartmentService.GetColumnConfig(4)
        .then(response => {
          const { serviceDisabledItems } = response.data || response;
          if (serviceDisabledItems && serviceDisabledItems.trim() !== "") {
            setDisabledColumns(serviceDisabledItems.split(',').map(item => parseInt(item.trim())));
          } else {
            setDisabledColumns([]);
          }
        })
        .catch(error => {
          console.error('Error fetching column configuration:', error);
          setDisabledColumns([]);
        });

      fetchFacultyStaffs();
    }
  , []);

  const fetchFacultyStaffs = () => {
    setIsLoading(true);
    FacultyStaffService.GetByDepartmentId()
      .then(response => {
        setFacultyStaffs(response.data);
        setIsLoading(false);
      })
      .catch(error => {
        setIsLoading(false);
        console.error('Error fetching page content:', error);
      });
  };

  const fetchOfficeHours = async (selectedTerm, userId, forAccordion = false) => {
    setIsLoading(!forAccordion);
    try {
      const response = await OfficeHourService.GetByDepartmentAndTermByMemberId(selectedTerm, userId);
      if (forAccordion) {
        setAccordionOfficeHours(response.data);
      } else {
        setOfficeHours(response.data);
      }
    } catch (error) {
      if (forAccordion) {
        setAccordionOfficeHours([]);
      } else {
        setOfficeHours([]);
      }
      console.error('Error fetching page content:', error);
    }
    setIsLoading(false);
  };


  const fetchClassSchedule = async (selectedTerm, userId, forAccordion = false) => {
    setIsLoading(!forAccordion);
    try {
      const response = await ClassScheduleService.GetByDepartmentAndTermByMemberId(selectedTerm, userId);
      if (forAccordion) {
        setAccordionClassSchedule(response.data);
      } else {
        setClassSchedule(response.data);
      }
    } catch (error) {
      if (forAccordion) {
        setAccordionClassSchedule([]);
      } else {
        setClassSchedule([]);
      }
      console.error('Error fetching page content:', error);
    }
    setIsLoading(false);
  };

  const filteredFacultyStaffs = facultyStaffs.filter(staff =>
    staff.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.userTypeSt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedFacultyStaffs = (staff) => {
    if (!sortConfig.key) return staff;

    return [...staff].sort((a, b) => {
      const aValue = (a[sortConfig.key] || '').toLowerCase();
      const bValue = (b[sortConfig.key] || '').toLowerCase();
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <FaSort className="ms-1 text-muted" />;
    }
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ms-1" />
      : <FaSortDown className="ms-1" />;
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = getSortedFacultyStaffs(filteredFacultyStaffs).slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const handleAccordionOfficeHours = async (staff) => {
    if (openAccordionType === 'office' && selectedFacultyId === staff.userId) {
      setOpenAccordionType(null);
      setSelectedFacultyId(null);
      return;
    }
    // Reset TermSelector if switching from class schedule
    if (openAccordionType === 'schedule') {
      setTermSelectorKey(prev => prev + 1);
    }
    setOpenAccordionType('office');
    setSelectedFacultyId(staff.userId);
    setAccordionLoading(true);
    setAccordionTerm(0);
    setAccordionOfficeHours([]);
    setAccordionTermDetails({});
    setHasLoadedInitialTerm(false);
    try {
      await fetchOfficeHours(0, staff.userId, true);
    } catch (e) {
      setAccordionOfficeHours([]);
    }
    setAccordionLoading(false);
  };

  const handleAccordionClassSchedule = async (staff) => {
    if (openAccordionType === 'schedule' && selectedFacultyId === staff.userId) {
      setOpenAccordionType(null);
      setSelectedFacultyId(null);
      return;
    }
    // Reset TermSelector if switching from office hours
    if (openAccordionType === 'office') {
      setTermSelectorKey(prev => prev + 1);
    }
    setOpenAccordionType('schedule');
    setSelectedFacultyId(staff.userId);
    setAccordionLoading(true);
    setAccordionTerm(0);
    setAccordionClassSchedule([]);
    setAccordionTermDetails({});
    setHasLoadedInitialTerm(false);
    try {
      await fetchClassSchedule(0, staff.userId, true);
    } catch (e) {
      setAccordionClassSchedule([]);
    }
    setAccordionLoading(false);
  };

  const handleAccordionTermChange = async (termId, termDetails, staff) => {
    // If this is the first real term load after opening, just set state, don't call API
    if (!hasLoadedInitialTerm) {
      setAccordionTerm(termId);
      setAccordionTermDetails(termDetails);
      setHasLoadedInitialTerm(true);
      return;
    }
    // For user-initiated changes, call API
    setAccordionTerm(termId);
    setAccordionTermDetails(termDetails);
    setAccordionLoading(true);
    try {
      if (openAccordionType === 'office') {
        await fetchOfficeHours(termId, staff.userId, true);
      } else if (openAccordionType === 'schedule') {
        await fetchClassSchedule(termId, staff.userId, true);
      }
    } catch (e) {
      if (openAccordionType === 'office') {
        setAccordionOfficeHours([]);
      } else if (openAccordionType === 'schedule') {
        setAccordionClassSchedule([]);
      }
    }
    setAccordionLoading(false);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container-fluid p-0" 
      style={{ backgroundColor: '#f8f9fa' }}
    >
      <div style={{ position: 'relative', zIndex: 1 }}>
      {/* <DepartmentBanner /> */}
        
        <div className="container-custom py-4">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="mb-4">
            <ol className="breadcrumb bg-transparent p-0">
              <li className="breadcrumb-item">
              </li>
              {/* <li className="breadcrumb-item active" aria-current="page">Faculty & Staff</li> */}
            </ol>
          </nav>

          {/* Stats Cards */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Total Faculty & Staff</p>
                  <p className="mb-0 h3">{facultyStaffs.length}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Faculty Members</p>
                  <p className="mb-0 h3">
                    {facultyStaffs.filter(staff => staff.userTypeSt.toLowerCase().includes('faculty')).length}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm stats-card">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Staff Members</p>
                  <p className="mb-0 h3">
                    {facultyStaffs.filter(staff => staff.userTypeSt.toLowerCase().includes('staff')).length}
                  </p>
                </div>
              </div>
            </div>
            
          </div>

          {/* Header Section */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-lg-3">
                  <p className="mb-0 h3">Faculty & Staff Directory</p>
                  <p className="text-muted mb-0">Browse our academic team</p>
                </div>
                <div className="col-lg-9">
                  <div className="d-flex gap-3 flex-wrap">
                    <div className="flex-grow-1">
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <FaSearch className="text-muted" />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0"
                          placeholder="Search faculty and staff..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="btn-group view-toggle">
                      <button type="button" aria-label="Open Table"
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('table')}
                      >
                        <FaTable />
                      </button>
                      <button type="button" aria-label="Open Grid"
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('grid')}
                      >
                        <FaTh />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <AnimatePresence mode="wait">
            {isLoading ? (
              <div className="text-center py-5">
                <p className="text-muted">Loading faculty and staff...</p>
              </div>
            ) : viewMode === 'table' ? (
              <motion.div
                key="table"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="card shadow-sm border-0"
              >
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr className="bg-light align-middle">
                          {isColumnEnabled(COLUMN_TYPES.NAME_AND_CONTACT) && (
                            <th 
                              className="px-2 py-3 ps-3 cursor-pointer" 
                              onClick={() => requestSort(SORT_KEYS.NAME)}
                            >
                              Name & Contact {getSortIcon(SORT_KEYS.NAME)}
                            </th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.CONTACT_INFORMATION) && (
                            <th 
                              className="px-2 py-3 cursor-pointer" 
                              onClick={() => requestSort(SORT_KEYS.CONTACT)}
                              style={{width: '30%'}}
                            >
                              Contact Information {getSortIcon(SORT_KEYS.CONTACT)}
                            </th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.ACTION) && (
                            <th className="px-2 py-3 text-center">Action</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? (
                          currentItems.map((staff, index) => (
                            <React.Fragment key={index}>
                              <motion.tr 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: index * 0.05 }}
                                className="align-middle"
                              >
                                {isColumnEnabled(COLUMN_TYPES.NAME_AND_CONTACT) && (
                                  <td className="px-2 py-3 ps-3">
                                    <p className="mb-1 h6">{staff.fullName}</p>
                                    <div className="text-muted">
                                      <a className={styles.link} href={"mailto:" + staff.email}>
                                        <FaEnvelope className="me-1" />
                                        {staff.email}
                                      </a>
                                      <br />
                                      <a className={styles.link} href={"tel:" + staff.officePhone}>
                                        <FaPhone className="me-1" />
                                        {staff.officePhone}
                                      </a>
                                      <br />
                                      <span className="badge bg-primary mt-1">
                                        <FaBriefcase className="me-1" />
                                        {staff.userTypeSt}
                                      </span>
                                    </div>
                                  </td>
                                )}
                                {isColumnEnabled(COLUMN_TYPES.CONTACT_INFORMATION) && (
                                  <td className="px-2 py-3">
                                    <span className="text-muted">
                                      {staff.contractInfo}
                                    </span>
                                  </td>
                                )}
                                {isColumnEnabled(COLUMN_TYPES.ACTION) && (
                                  <td className="px-2 py-3 text-center align-middle">
                                    <div className="d-inline-flex gap-2 align-items-center justify-content-center">
                                      <button
                                        className={`btn btn-sm ${openAccordionType === 'office' && selectedFacultyId === staff.userId ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => handleAccordionOfficeHours(staff)}
                                      >
                                        Office Hours
                                      </button>
                                      <button 
                                        className={`btn btn-sm ${openAccordionType === 'schedule' && selectedFacultyId === staff.userId ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => handleAccordionClassSchedule(staff)}
                                      >
                                        Class Schedule
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </motion.tr>
                              {/* Accordion Row */}
                              {selectedFacultyId === staff.userId && (
                                <tr>
                                  <td colSpan={Object.values(COLUMN_TYPES).filter(type => isColumnEnabled(type)).length} className="bg-light p-0 border-0">
                                    <div className="p-4">
                                      <div className="d-flex flex-wrap align-items-baseline gap-3">
                                        <div className="d-flex align-items-baseline">
                                          <TermSelector 
                                            key={termSelectorKey} 
                                            onTermChangeEvent={(termId, termDetails) => handleAccordionTermChange(termId, termDetails, staff)} 
                                          />
                                        </div>
                                        <div className="d-flex align-items-baseline">
                                          <div className="fw-bold me-2">Current Term:</div>
                                          <div>{accordionTermDetails.termWithYearLast || 'Not Selected'}</div>
                                        </div>
                                      </div>
                                      {accordionLoading ? (
                                        <div className="text-center py-5">
                                          <p className="text-muted">Loading {openAccordionType === 'office' ? 'office hours' : 'class schedule'}...</p>
                                        </div>
                                      ) : openAccordionType === 'office' ? (
                                        <div className="table-responsive">
                                          {accordionOfficeHours && accordionOfficeHours.length > 0 && accordionOfficeHours[0].officeHours && accordionOfficeHours[0].officeHours.length > 0 ? (
                                            <table className="table table-bordered mb-0">
                                              <thead>
                                                <tr>
                                                  <th>Office Day</th>
                                                  <th>Office Hour</th>
                                                  <th>Location</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {accordionOfficeHours[0].officeHours.map((hour, idx) => (
                                                  <tr key={idx}>
                                                    <td>{hour.dayofweek}</td>
                                                    <td>{hour.discription}</td>
                                                    <td>{accordionOfficeHours[0].Instructor.officeAddress}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          ) : (
                                            <div className="text-center py-3">No Office Hour found</div>
                                          )}
                                        </div>
                                      ) : openAccordionType === 'schedule' ? (
                                        <div className="table-responsive">
                                          {accordionClassSchedule && accordionClassSchedule.length > 0 && accordionClassSchedule[0].schedule ? (
                                            <table className="table table-bordered mb-0">
                                              <thead>
                                                <tr>
                                                  <th>Time</th>
                                                  <th>Monday</th>
                                                  <th>Tuesday</th>
                                                  <th>Wednesday</th>
                                                  <th>Thursday</th>
                                                  <th>Friday</th>
                                                  <th>Saturday</th>
                                                  <th>Sunday</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {accordionClassSchedule[0].schedule.map((slot, idx) => (
                                                  <tr key={idx}>
                                                    <td>{slot.time}</td>
                                                    <td>{slot.monday}</td>
                                                    <td>{slot.tuesday}</td>
                                                    <td>{slot.wednesday}</td>
                                                    <td>{slot.thursday}</td>
                                                    <td>{slot.friday}</td>
                                                    <td>{slot.saturday}</td>
                                                    <td>{slot.sunday}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          ) : (
                                            <div className="text-center py-3">No Class Schedule found</div>
                                          )}
                                        </div>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={Object.values(COLUMN_TYPES).filter(type => isColumnEnabled(type)).length} className="text-center py-5">
                              <p className="text-muted mb-0">No faculty or staff found</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="row g-4"
              >
                {currentItems.map((staff, index) => (
                  <motion.div
                    key={index}
                    className="col-md-6 col-lg-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="card h-100 border-0 shadow-sm hover-shadow">
                      <div className="card-body">
                        {isColumnEnabled(COLUMN_TYPES.NAME_AND_CONTACT) && (
                          <>
                            <div className="d-flex align-items-center mb-3">
                              <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                                <FaUserTie className="text-primary" size={24} />
                              </div>
                              <div>
                                <h5 className="card-title mb-1">{staff.fullName}</h5>
                                <span className="badge bg-primary">
                                  {staff.userTypeSt}
                                </span>
                              </div>
                            </div>
                            <div className="mb-3">
                              <a className={styles.link} href={"mailto:" + staff.email}>
                                <FaEnvelope className="me-2" />
                                {staff.email}
                              </a>
                            </div>
                            <div className="mb-3">
                              <a className={styles.link} href={"tel:" + staff.officePhone}>
                                <FaPhone className="me-2" />
                                {staff.officePhone}
                              </a>
                            </div>
                          </>
                        )}
                        {isColumnEnabled(COLUMN_TYPES.CONTACT_INFORMATION) && (
                          <div className="text-muted">
                            {staff.contractInfo}
                          </div>
                        )}
                        {isColumnEnabled(COLUMN_TYPES.ACTION) && (
                          <div className="d-flex gap-2 mt-3">
                            <button 
                              className={`btn btn-sm ${openAccordionType === 'office' && selectedFacultyId === staff.userId ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => handleAccordionOfficeHours(staff)}
                            >
                              Office Hours
                            </button>
                            <button 
                              className={`btn btn-sm ${openAccordionType === 'schedule' && selectedFacultyId === staff.userId ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => handleAccordionClassSchedule(staff)}
                            >
                              Class Schedule
                            </button>
                          </div>
                        )}
                        {selectedFacultyId === staff.userId && (
                          <div className="mt-3 border-top pt-3">
                            <div className="d-flex flex-wrap align-items-baseline gap-3 mb-3">
                              <div className="d-flex align-items-baseline">
                                <TermSelector 
                                  key={termSelectorKey} 
                                  onTermChangeEvent={(termId, termDetails) => handleAccordionTermChange(termId, termDetails, staff)} 
                                />
                              </div>
                              <div className="d-flex align-items-baseline">
                                <div className="fw-bold me-2">Current Term:</div>
                                <div>{accordionTermDetails.termWithYearLast || 'Not Selected'}</div>
                              </div>
                            </div>
                            {accordionLoading ? (
                              <div className="text-center py-3">
                                <p className="text-muted">Loading {openAccordionType === 'office' ? 'office hours' : 'class schedule'}...</p>
                              </div>
                            ) : openAccordionType === 'office' ? (
                              <div className="table-responsive">
                                {accordionOfficeHours && accordionOfficeHours.length > 0 && accordionOfficeHours[0].officeHours && accordionOfficeHours[0].officeHours.length > 0 ? (
                                  <table className="table table-bordered mb-0">
                                    <thead>
                                      <tr>
                                        <th>Office Day</th>
                                        <th>Office Hour</th>
                                        <th>Location</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {accordionOfficeHours[0].officeHours.map((hour, idx) => (
                                        <tr key={idx}>
                                          <td>{hour.dayofweek}</td>
                                          <td>{hour.discription}</td>
                                          <td>{accordionOfficeHours[0].Instructor.officeAddress}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <div className="text-center py-3">No Office Hour found</div>
                                )}
                              </div>
                            ) : openAccordionType === 'schedule' ? (
                              <div className="table-responsive">
                                {accordionClassSchedule && accordionClassSchedule.length > 0 && accordionClassSchedule[0].schedule ? (
                                  <table className="table table-bordered mb-0">
                                    <thead>
                                      <tr>
                                        <th>Time</th>
                                        <th>Monday</th>
                                        <th>Tuesday</th>
                                        <th>Wednesday</th>
                                        <th>Thursday</th>
                                        <th>Friday</th>
                                        <th>Saturday</th>
                                        <th>Sunday</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {accordionClassSchedule[0].schedule.map((slot, idx) => (
                                        <tr key={idx}>
                                          <td>{slot.time}</td>
                                          <td>{slot.monday}</td>
                                          <td>{slot.tuesday}</td>
                                          <td>{slot.wednesday}</td>
                                          <td>{slot.thursday}</td>
                                          <td>{slot.friday}</td>
                                          <td>{slot.saturday}</td>
                                          <td>{slot.sunday}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <div className="text-center py-3">No Class Schedule found</div>
                                )}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                {currentItems.length === 0 && (
                  <div className="col-12 text-center py-5">
                    <p className="text-muted mb-0">No faculty or staff found</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalItems={getSortedFacultyStaffs(filteredFacultyStaffs).length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default FacultyAndStaff;
